import { type EnvironmentProviders, InjectionToken, makeEnvironmentProviders, type Provider } from '@angular/core';
import { createMmscanI18n, type MmscanI18nFactory } from '@mmscan/angular-ui/core';

/**
 * Strings rendered by the PDF viewer. Placeholders in braces are replaced:
 * `{current}` / `{total}` in `matchCount`, `{page}` / `{total}` in `page`, `{percent}` in `resetZoom`.
 */
export interface MmscanPdfViewerI18n {
  /** Accessible name of the toolbar. */
  toolbar: string;
  /** Accessible name of the scrollable document area. */
  document: string;
  /** Search field label and default placeholder. */
  search: string;
  clearSearch: string;
  previousMatch: string;
  nextMatch: string;
  matchCount: string;
  noMatches: string;
  page: string;
  /** Accessible name of each page region. */
  pageRegion: string;
  zoomIn: string;
  zoomOut: string;
  resetZoom: string;
  download: string;
  print: string;
  loadError: string;
  /** Shown when the browser blocks the print pop-up. */
  printBlocked: string;
}

export const MMSCAN_PDF_VIEWER_I18N_DEFAULTS: MmscanPdfViewerI18n = {
  toolbar: 'PDF tools',
  document: 'PDF document',
  search: 'Search',
  clearSearch: 'Clear search',
  previousMatch: 'Previous match',
  nextMatch: 'Next match',
  matchCount: '{current} of {total} matches',
  noMatches: 'No matches',
  page: 'Page {page} of {total}',
  pageRegion: 'Page {page}',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  resetZoom: '{percent}% – fit to width',
  download: 'Download',
  print: 'Print',
  loadError: 'The PDF could not be loaded.',
  printBlocked: 'Allow pop-ups to print this PDF, or download it and print it yourself.',
};

const pdfViewerI18n = createMmscanI18n<MmscanPdfViewerI18n>('MMSCAN_PDF_VIEWER_I18N', MMSCAN_PDF_VIEWER_I18N_DEFAULTS);

export const MMSCAN_PDF_VIEWER_I18N = pdfViewerI18n.token;

/** Where pdf.js finds its worker and character maps. */
export interface MmscanPdfViewerConfig {
  /** URL of `pdf.worker.min.mjs`, served by the app (copy it from `pdfjs-dist/build`). */
  workerSrc: string;
  /** Base URL of the pdf.js CMaps (needed for some CJK fonts). Defaults to jsDelivr for the loaded pdf.js version. */
  cMapUrl?: string;
}

export const MMSCAN_PDF_VIEWER_CONFIG = new InjectionToken<MmscanPdfViewerConfig>('MMSCAN_PDF_VIEWER_CONFIG', {
  providedIn: 'root',
  factory: () => ({ workerSrc: '/pdf.worker.min.mjs' }),
});

export interface MmscanPdfViewerOptions {
  /** Defaults to `/pdf.worker.min.mjs`. */
  workerSrc?: string;
  /** Defaults to `https://cdn.jsdelivr.net/npm/pdfjs-dist@<version>/cmaps/`. */
  cMapUrl?: string;
  /** Translated viewer strings; same contract as `provideMmscanUi({ i18n })`. */
  i18n?: MmscanI18nFactory<MmscanPdfViewerI18n>;
}

export function provideMmscanPdfViewer(options: MmscanPdfViewerOptions = {}): EnvironmentProviders {
  const providers: Provider[] = [
    {
      provide: MMSCAN_PDF_VIEWER_CONFIG,
      useValue: { workerSrc: options.workerSrc ?? '/pdf.worker.min.mjs', cMapUrl: options.cMapUrl },
    },
  ];
  if (options.i18n) providers.push(pdfViewerI18n.provide(options.i18n));
  return makeEnvironmentProviders(providers);
}
